<script>
  import { onMount } from 'svelte'

  import { styleVars, generateBorderRadiusString, canUseWebP } from '/helper'
  import Link, { navigate } from '/components/Atoms/Link'
  import IconInfo from '/Icons/IconInfoRed'

  export let bgImage = ""
  export let bgImage2x = ""
  export let bgImageWebp = ""
  export let bgImageWebp2x = ""
  export let group = "default" // a key to coordniate the combinations ob border radius and columns
  export let bgColorKey = null
  export let borderRadiusPreset = undefined
  export let nohole = false
  export let standardRadius = false
  export let flow = false // true -> height: 100%
  export let link = null
  export let imageContain = false
  export let verticalPadding = false
  export let paddingStyle = false
  export let info = false

  bgColorKey = bgColorKey || "white"

  let webp = !!bgImageWebp

  if (info) nohole = true

  let borderRadiusStringLarge, borderRadiusStringSmall

  function mapPadding(style) {
    
    if (style === "moon") return [28, 15]
    if (style === "list") return [30, 22]
    if (style === "article") return [30,20]
    if (style === "headline") return [19, 12]
    if (style === "fromalmanac") return [26,17]
  }

  if (paddingStyle) verticalPadding = mapPadding(paddingStyle)

  // $: console.log(bgImage)

  onMount(function() {
    webp = !!bgImageWebp && canUseWebP()
    if (standardRadius) {
      borderRadiusPreset = {
        small: [15,15,15,15],
        large: [20,20,20,20]
      }
    }
    const {largeString, smallString} = generateBorderRadiusString(group, borderRadiusPreset)
    borderRadiusStringLarge = largeString
    borderRadiusStringSmall = smallString
  })

  function onClick() {
    if (link) {
      navigate(link)
    }
  }

</script>

<div 
  class="container bg-color-{bgColorKey}" 
  class:bgImage={bgImage}
  class:bgImage2x={bgImage2x}
  class:webp
  class:nohole
  class:flow
  class:link
  class:imageContain
  class:verticalPadding
  style={styleVars({
    imageUrl: `url("${webp ? bgImageWebp : bgImage}")`,
    imageUrl2x: `url("${webp ? bgImageWebp2x : bgImage2x}")`,
    borderRadiusStringLarge,
    borderRadiusStringSmall,
    [verticalPadding && "verticalPaddingLarge"]: verticalPadding?.[0]+"px",
    [verticalPadding && "verticalPaddingSmall"]: verticalPadding?.[1]+"px",
  })}
>
  {#if link}
    <Link {...link} hover="none" stretch>
      <slot></slot>
    </Link>
  {:else}
    <slot></slot>
  {/if}
  {#if info}
    <div class="info">
      <IconInfo />
    </div>
  {/if}
</div>

<style lang="scss">

  $hole-radius-large: 0.5 * $hole-diameter-large;
  $hole-radius-small: 0.5 * $hole-diameter-small;

  .container {
    overflow: hidden;
    width: 100%;
    &:not(.flow) {
      height: 100%;
    }
    display: flex;
    flex-direction: column;
    justify-content: center;
    box-sizing: border-box;

    padding: 14px 30px;
    &.verticalPadding {
      padding-top: var(--verticalPaddingLarge);
      padding-bottom: var(--verticalPaddingLarge);
      @include media-small {
        padding-top: var(--verticalPaddingSmall);
        padding-bottom: var(--verticalPaddingSmall);
      }
    }
    min-height: #{ $hole-distance-large + $hole-diameter-large};
    border-radius: var(--borderRadiusStringLarge);
    @include media-small {
      padding: 10px 20px;
      min-height: #{ $hole-distance-small + $hole-diameter-small};
      border-radius: var(--borderRadiusStringSmall);
    }
    @include media-larger {
      padding: 10px 40px;
      min-height: #{ $hole-distance-small + $hole-diameter-small};
      border-radius: var(--borderRadiusStringSmall);
    }
    box-shadow: $shadow;
    position: relative;
    /*backdrop-filter: blur(4px);*/
    &.link {
      cursor: pointer;
      &:hover {
        box-shadow: $link-hover-shadow;
      }
    }
  }

  // background in varying colors, including a transparent hole
  @each $name, $color in $card-bg-colors {
    .container:not(.nohole).bg-color-#{$name} {
      
      background: radial-gradient(
        circle at calc(100% - #{$hole-distance-large + $hole-radius-large}) #{$hole-distance-large + $hole-radius-large}, 
        transparent $hole-radius-large, 
        $color $hole-radius-large
      );

      @include media-small {
        background: radial-gradient(
        circle at calc(100% - #{$hole-distance-small + $hole-radius-small}) #{$hole-distance-small + $hole-radius-small}, 
        transparent $hole-radius-small, 
        $color $hole-radius-small
        );
      }

    }

    // overwrite in case of nohole
    .container.nohole.bg-color-#{$name} {
      background-color: $color;
    }

    //add simple bg color if an image of type contain is there
    .container.bgImage.imageContain.bg-color-#{$name} {
      background-color: $color;
    }

  }

  

  // optional background image (no hole in this case)
  .container.bgImage {
    background: var(--imageUrl);
    background-size: cover;
    background-position: center;
    &.imageContain {
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
    }


    &.bgImage2x {
      @media 
      (-webkit-min-device-pixel-ratio: 2), 
      (min-resolution: 192dpi) { 
        background: var(--imageUrl2x);
        background-size: cover;
        background-position: center;
        &.imageContain {
          background-size: contain;
          background-repeat: no-repeat;
          background-position: center;
        }
      }
    }
    &::after {
      display: none;
    }
  }

  // the drop shadow inside the hole
  .container:not(.nohole)::after {
    content: "";
    border-radius: 50%;
    @include box-shadow-inset;
    position: absolute;

    right: $hole-distance-large;
    top: $hole-distance-large;    
    height: $hole-diameter-large;
    width: $hole-diameter-large;
    @include media-small {
      right: $hole-distance-small;
      top: $hole-distance-small;
      height: $hole-diameter-small;
      width: $hole-diameter-small;
    }
  }

  // optional info icon
  .info {
    position: absolute;
    right: #{$hole-distance-large + $hole-radius-large};
    top: #{$hole-distance-large};
    width: $hole-radius-large;
    height: $hole-radius-large;
    @include media-small { 
      right: #{$hole-distance-small + $hole-radius-small};
      top: #{$hole-distance-small};
      width: $hole-radius-small;
      height: $hole-radius-small;
    }
  }

</style>